import { useMemo } from 'react';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import { useQuotaBalances } from '@/state/queries/entitlements/use-quota-balances';
import { resolveRefId } from '@/helpers/object.id.helper';
import { resolveFlowDurationMinutes } from '@/helpers/flow-playout-hours.helper';

const QUOTA_PLAYOUT_HOURS = 'playout_hours';

export type InsufficientPlayoutHoursInfo = {
  hours: number;
  minutes: number;
  /** Formatted granted playout hours for i18n. */
  playoutHoursLabel: string;
  playoutHours: number;
  flowMinutes: number;
  /** Origin (or self) flow id used for the Flow-scoped balance query. */
  flowScopeId: string;
};

function formatHoursLabel(hours: number): string {
  if (!Number.isFinite(hours)) return '0';
  const rounded = Math.round(hours * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

export type PlayoutHoursCheck = {
  /** Deficit details, or `null` when coverage is enough / not yet known. */
  info: InsufficientPlayoutHoursInfo | null;
  /** Balances are still resolving — a `null` info must not be read as "sufficient" yet. */
  isChecking: boolean;
};

/**
 * Flow-scoped `playout_hours` vs planned flow length (same rule as BasePlanCapacityUsage).
 * Slot / EventSpace scopes can be layered in later.
 */
export function useInsufficientPlayoutHoursInfo(flow: FlowDto | undefined | null): PlayoutHoursCheck {
  const flowScopeId = flow ? (resolveRefId(flow.origin) ?? flow.id) : undefined;
  const { data: balances, isSuccess, isError } = useQuotaBalances(flowScopeId ? 'Flow' : undefined, flowScopeId);

  const info = useMemo(() => {
    if (!flow || !flowScopeId || !isSuccess) return null;

    const playout = balances?.find((b) => b.quotaKey === QUOTA_PLAYOUT_HOURS);
    const playoutHours = playout?.totalGranted ?? 0;
    const flowMinutes = Math.max(0, Math.round(resolveFlowDurationMinutes(flow)));
    if (!playout && flowMinutes <= 0) return null;

    const flowHours = flowMinutes / 60;
    if (!(flowHours > playoutHours + 1e-9)) return null;

    return {
      hours: Math.floor(flowMinutes / 60),
      minutes: flowMinutes % 60,
      playoutHoursLabel: formatHoursLabel(playoutHours),
      playoutHours,
      flowMinutes,
      flowScopeId,
    };
  }, [balances, flow, flowScopeId, isSuccess]);

  // A failed balances query stays out of `isChecking` so a transient error cannot lock go-live.
  return { info, isChecking: Boolean(flowScopeId) && !isSuccess && !isError };
}
